import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  memo,
} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AppButton from '../../questionnaire/components/AppButton';
import CheckinLayout from '../../questionnaire/components/CheckinLayout';
import SignatureCanvas, {
  SignatureCanvasHandle,
} from '../../questionnaire/components/SignatureCanvas';
import { Alert } from '../../components/shared/Alert';
import axiosInstance from '../../utils/axiosConfig';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/apiError';
import { useWorkflow } from '../../questionnaire/contexts/WorkflowContext';
import { useLoader } from '../../questionnaire/contexts/LoaderContext';
import { WorkflowType } from '../../questionnaire/api/athena';
import { getFieldLabel, getGroupLabel } from '../../questionnaire/utils/formLabel';
import DOMPurify from 'dompurify';

interface ConsentForm {
  id: string;
  name: string;
  htmlContent: string;
  signedByPatientText: string;
  signedOnBehalfOfPatientText: string;
  displayOrder: number;
  required?: boolean;
}

interface ConsentFormsResponse {
  consentForms: ConsentForm[];
}

interface FormElementInfo {
  identifier: string;        // name or id for checkboxes, name for radio groups
  isRequired: boolean;       // has required attribute
  type: 'checkbox' | 'radio';
  value?: string;           // value attribute (for radio buttons)
  groupName?: string;       // radio group name
}

interface ParsedFormElements {
  checkboxes: FormElementInfo[];
  radioGroups: Map<string, FormElementInfo[]>;  // groupName -> radio buttons
  mandatoryCheckboxes: string[];                // identifiers of required checkboxes
  mandatoryRadioGroups: string[];                // names of required radio groups
  mandatoryCheckboxGroups: Map<string, string[]>; // groupName -> checkbox identifiers (at least one required)
  fieldLabels: Map<string, string>;             // identifier -> human-readable label
  groupLabels: Map<string, string>;             // checkbox group name -> human-readable label
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// Utility functions for parsing form elements
function extractCheckboxes(doc: Document): {
  checkboxes: FormElementInfo[];
  mandatoryCheckboxes: string[];
  mandatoryCheckboxGroups: Map<string, string[]>;
  fieldLabels: Map<string, string>;
  groupLabels: Map<string, string>;
} {
  const checkboxes: FormElementInfo[] = [];
  const mandatoryCheckboxes: string[] = [];
  const mandatoryCheckboxGroups = new Map<string, string[]>();
  const fieldLabels = new Map<string, string>();
  const groupLabels = new Map<string, string>();

  const checkboxElements = doc.querySelectorAll('input[type="checkbox"]');

  checkboxElements.forEach((cb: Element) => {
    const checkbox = cb as HTMLInputElement;
    // Prefer name attribute, fallback to id
    const identifier = checkbox.getAttribute('name') || checkbox.getAttribute('id');
    if (!identifier) {
      console.warn('Checkbox found without name or id attribute');
      return;
    }

    const isRequired = checkbox.hasAttribute('required');
    const requiredGroup = checkbox.getAttribute('data-required-group');

    checkboxes.push({
      identifier,
      isRequired,
      type: 'checkbox'
    });

    fieldLabels.set(identifier, getFieldLabel(checkbox, identifier));

    if (isRequired) {
      mandatoryCheckboxes.push(identifier);
    }

    if (requiredGroup) {
      if (!mandatoryCheckboxGroups.has(requiredGroup)) {
        mandatoryCheckboxGroups.set(requiredGroup, []);
      }
      mandatoryCheckboxGroups.get(requiredGroup)!.push(identifier);
      if (!groupLabels.has(requiredGroup)) {
        groupLabels.set(requiredGroup, getGroupLabel(doc, requiredGroup));
      }
    }
  });

  return { checkboxes, mandatoryCheckboxes, mandatoryCheckboxGroups, fieldLabels, groupLabels };
}

function extractRadioButtons(doc: Document): {
  radioGroups: Map<string, FormElementInfo[]>;
  mandatoryRadioGroups: string[];
} {
  const radioGroups = new Map<string, FormElementInfo[]>();
  const mandatoryRadioGroups: string[] = [];
  const processedGroups = new Set<string>();
  
  const radioElements = doc.querySelectorAll('input[type="radio"]');
  
  radioElements.forEach((radio: Element) => {
    const radioInput = radio as HTMLInputElement;
    const groupName = radioInput.getAttribute('name');
    if (!groupName) {
      console.warn('Radio button found without name attribute');
      return;
    }
    
    const value = radioInput.getAttribute('value') || '';
    const isRequired = radioInput.hasAttribute('required');
    const identifier = radioInput.getAttribute('id') || `${groupName}_${value}`;
    
    if (!radioGroups.has(groupName)) {
      radioGroups.set(groupName, []);
    }
    
    radioGroups.get(groupName)!.push({
      identifier,
      isRequired,
      type: 'radio',
      value,
      groupName
    });
    
    // Track mandatory groups (only add once per group)
    if (isRequired && !processedGroups.has(groupName)) {
      mandatoryRadioGroups.push(groupName);
      processedGroups.add(groupName);
    }
  });
  
  return { radioGroups, mandatoryRadioGroups };
}

function parseFormElements(htmlContent: string): ParsedFormElements {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  const { checkboxes, mandatoryCheckboxes, mandatoryCheckboxGroups, fieldLabels, groupLabels } = extractCheckboxes(doc);
  const { radioGroups, mandatoryRadioGroups } = extractRadioButtons(doc);

  return {
    checkboxes,
    radioGroups,
    mandatoryCheckboxes,
    mandatoryRadioGroups,
    mandatoryCheckboxGroups,
    fieldLabels,
    groupLabels,
  };
}

// Memoized form item component to prevent unnecessary re-renders
interface ConsentFormItemProps {
  form: ConsentForm;
  isOpen: boolean;
  isSigned: boolean;
  signerType: 'patient' | 'proxy';
  formValidationErrors?: string[];
  signingForms: Set<string>;
  onToggleOpen: () => void;
  onSign: (formId: string) => void;
  contentRef: (el: HTMLDivElement | null) => void;
  signingTextRef: (el: HTMLDivElement | null) => void;
  signatureRef: (el: SignatureCanvasHandle | null) => void;
  errorAnchorRef: (el: HTMLDivElement | null) => void;
}

const ConsentFormItem = memo<ConsentFormItemProps>(({
  form,
  isOpen,
  isSigned,
  signerType,
  formValidationErrors,
  signingForms,
  onToggleOpen,
  onSign,
  contentRef,
  signingTextRef,
  signatureRef,
  errorAnchorRef,
}) => {
  const signingText =
    signerType === 'patient'
      ? form.signedByPatientText
      : form.signedOnBehalfOfPatientText;
  const required = form.required !== false;

  return (
    <div
      className={`border rounded-xl overflow-hidden ${isOpen ? 'border-[#01545D]' : 'border-[#A4BDC0]'}`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DCE1E8] bg-white">
        <div className="flex items-center gap-3">
          <span className="text-base font-semibold text-[#1C1C1C]">
            {form.name}
          </span>
          {required && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-[#FA4D5E] text-white">
              Required
            </span>
          )}
          {isSigned && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-[#22C55E] text-white">
              Signed
            </span>
          )}
        </div>
        <button
          type="button"
          className="text-sm font-bold text-[#01545D] cursor-pointer"
          onClick={onToggleOpen}
        >
          {isOpen ? 'Close' : 'Read'}
        </button>
      </div>
      {isOpen && (
        <div className="bg-[#F7F8FD] px-3 py-4 sm:px-6 sm:py-5 flex flex-col gap-4">
          <div ref={errorAnchorRef} />
          {/* Display validation errors for this form */}
          {formValidationErrors && formValidationErrors.length > 0 && (
            <div className="bg-[#FEE] border border-[#FCC] rounded-lg px-4 py-3">
              <h3 className="text-sm font-semibold text-[#C00] mb-2">
                Please fix the following errors:
              </h3>
              <ul className="list-disc list-inside text-sm text-[#C00] space-y-1">
                {formValidationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          <div
            ref={contentRef}
            className="bg-white border border-[#DCE1E8] rounded-lg px-3 py-4 sm:px-5 sm:py-5 overflow-x-auto consent-form-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.htmlContent) }}
          />
          <div
            ref={signingTextRef}
            className="bg-white border border-[#DCE1E8] rounded-lg px-3 py-4 sm:px-5 sm:py-5 overflow-x-auto signing-section"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(signingText) }}
          />
          <SignatureCanvas ref={signatureRef} />
          {!isSigned && (
            <button
              type="button"
              onClick={() => onSign(form.id)}
              disabled={signingForms.has(form.id)}
              className="mt-2 px-4 py-2 bg-[#01545D] text-white rounded-md font-semibold text-sm hover:bg-[#0b7981] disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {signingForms.has(form.id) ? 'Signing...' : 'Sign Consent Form'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo - return true if props are equal (skip re-render)
  const formId = prevProps.form.id;
  const prevErrors = prevProps.formValidationErrors || [];
  const nextErrors = nextProps.formValidationErrors || [];
  
  return (
    prevProps.form.id === nextProps.form.id &&
    prevProps.form.name === nextProps.form.name &&
    prevProps.form.htmlContent === nextProps.form.htmlContent &&
    prevProps.form.signedByPatientText === nextProps.form.signedByPatientText &&
    prevProps.form.signedOnBehalfOfPatientText === nextProps.form.signedOnBehalfOfPatientText &&
    prevProps.form.required === nextProps.form.required &&
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.isSigned === nextProps.isSigned &&
    prevProps.signerType === nextProps.signerType &&
    prevProps.signingForms.has(formId) === nextProps.signingForms.has(formId) &&
    prevErrors.length === nextErrors.length &&
    prevErrors.every((err, i) => err === nextErrors[i])
  );
});

ConsentFormItem.displayName = 'ConsentFormItem';

const ReviewAndSign: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError, loading: workflowLoading, markStepCompleted, refreshWorkflow } = useWorkflow();
  const { show, hide, withLoader } = useLoader();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setErrorInternal] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState(0);
  const setError = useCallback((msg: string | null) => {
    setErrorInternal(msg);
    if (msg !== null) setErrorKey((k) => k + 1);
  }, []);
  const [signerType, setSignerType] = useState<'patient' | 'proxy'>('patient');
  const [consentForms, setConsentForms] = useState<ConsentForm[]>([]);
  const [signedForms, setSignedForms] = useState<Set<string>>(new Set());
  const [submittedForms, setSubmittedForms] = useState<Set<string>>(new Set());
  const [signingForms, setSigningForms] = useState<Set<string>>(new Set());
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [parsedElements, setParsedElements] = useState<Record<string, ParsedFormElements>>({});
  const [checkboxStates, setCheckboxStates] = useState<Record<string, Record<string, boolean>>>({});
  const [radioSelections, setRadioSelections] = useState<Record<string, Record<string, string>>>({});
  const [formValidationErrors, setFormValidationErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const initialLoading = workflowLoading || loading;
    if (initialLoading) show("Loading checkin...");
    else hide();
    return () => hide();
  }, [workflowLoading, loading, show, hide]);

  const signatureRefs = useRef<Record<string, SignatureCanvasHandle>>({});
  const contentRefs = useRef<Record<string, HTMLDivElement>>({});
  const signingTextRefs = useRef<Record<string, HTMLDivElement>>({});
  const errorAnchorRefs = useRef<Record<string, HTMLDivElement>>({});
  const pageTopRef = useRef<HTMLDivElement>(null);
  // Store event listener cleanup functions to prevent memory leaks
  const eventListenerCleanups = useRef<Record<string, Array<() => void>>>({});
  // Track which forms have already had placeholders replaced to avoid losing input values
  const processedPlaceholders = useRef<Record<string, { content: boolean; signing: boolean }>>({});
  const inFlightSignFormsRef = useRef<Set<string>>(new Set());

  const allForms: ConsentForm[] = consentForms;

  useEffect(() => {
    if (!appointmentId) {
      setError('Missing appointment id in URL.');
      return;
    }

    let cancelled = false;
    setLoading(true);
    
    axiosInstance.get(
      `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/consent-forms`,
      { headers: { client: 'axis', domain: 'axis' } }
    )
      .then((response) => {
        const data = response.data as ConsentFormsResponse;
        if (!cancelled) {
          const sortedForms = data.consentForms.sort((a, b) => a.displayOrder - b.displayOrder);
          setConsentForms(sortedForms);
          
          // Parse form elements for all forms
          const newParsedElements: Record<string, ParsedFormElements> = {};
          const newCheckboxStates: Record<string, Record<string, boolean>> = {};
          const newRadioSelections: Record<string, Record<string, string>> = {};
          
          sortedForms.forEach((form) => {
            // Parse both htmlContent and signing text
            const contentElements = parseFormElements(form.htmlContent);
            const signingElements = parseFormElements(
              signerType === 'patient' ? form.signedByPatientText : form.signedOnBehalfOfPatientText
            );
            
            // Merge elements from both sources
            const mergedRadioGroups = new Map<string, FormElementInfo[]>();
            contentElements.radioGroups.forEach((radios, groupName) => {
              mergedRadioGroups.set(groupName, radios);
            });
            signingElements.radioGroups.forEach((radios, groupName) => {
              const existing = mergedRadioGroups.get(groupName) || [];
              mergedRadioGroups.set(groupName, [...existing, ...radios]);
            });

            const mergedCheckboxGroups = new Map<string, string[]>();
            contentElements.mandatoryCheckboxGroups.forEach((ids, groupName) => {
              mergedCheckboxGroups.set(groupName, [...ids]);
            });
            signingElements.mandatoryCheckboxGroups.forEach((ids, groupName) => {
              const existing = mergedCheckboxGroups.get(groupName) || [];
              mergedCheckboxGroups.set(groupName, [...existing, ...ids]);
            });

            const mergedElements: ParsedFormElements = {
              checkboxes: [...contentElements.checkboxes, ...signingElements.checkboxes],
              radioGroups: mergedRadioGroups,
              mandatoryCheckboxes: [...contentElements.mandatoryCheckboxes, ...signingElements.mandatoryCheckboxes],
              mandatoryRadioGroups: [...contentElements.mandatoryRadioGroups, ...signingElements.mandatoryRadioGroups],
              mandatoryCheckboxGroups: mergedCheckboxGroups,
              fieldLabels: (() => { const m = new Map(contentElements.fieldLabels); signingElements.fieldLabels.forEach((v, k) => m.set(k, v)); return m; })(),
              groupLabels: (() => { const m = new Map(contentElements.groupLabels); signingElements.groupLabels.forEach((v, k) => m.set(k, v)); return m; })(),
            };
            
            newParsedElements[form.id] = mergedElements;
            
            // Initialize checkbox states (all unchecked by default)
            const initialCheckboxStates: Record<string, boolean> = {};
            mergedElements.checkboxes.forEach(cb => {
              initialCheckboxStates[cb.identifier] = false;
            });
            newCheckboxStates[form.id] = initialCheckboxStates;
            
            // Initialize radio selections (empty by default)
            newRadioSelections[form.id] = {};
          });
          
          setParsedElements(newParsedElements);
          setCheckboxStates(newCheckboxStates);
          setRadioSelections(newRadioSelections);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'Unable to load consent forms. Please try again.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, signerType]);

  const camelCaseToPlaceholder = useCallback((camelCase: string): string => {
    const result = camelCase
      // Replace underscores with spaces first
      .replace(/_/g, ' ')
      // Add space before uppercase letters (camelCase handling)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Add space between consecutive uppercase letters followed by lowercase
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .toLowerCase()
      .split(' ')
      .filter((word) => word.length > 0) // Remove empty strings from multiple spaces
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return result;
  }, []);

  const replacePlaceholdersWithInputs = useCallback(
    (container: HTMLDivElement | null, formId: string, containerType: 'content' | 'signing') => {
      if (!container) return;

      // Check if we've already processed placeholders for this container
      const key = `${formId}_${containerType}`;
      if (processedPlaceholders.current[key]) {
        return; // Already processed, don't replace innerHTML to preserve input values
      }

      const html = container.innerHTML;
      const regex = /\{\{(\w+)\}\}/g;
      let match;
      const replacements: Array<{ placeholder: string; name: string }> = [];

      while ((match = regex.exec(html)) !== null) {
        const placeholder = match[0];
        const name = match[1];
        replacements.push({ placeholder, name });
      }

      // Only process if there are placeholders to replace
      if (replacements.length === 0) {
        processedPlaceholders.current[key] = { content: containerType === 'content', signing: containerType === 'signing' };
        return;
      }

      // Batch DOM updates: build replacement map first, then apply all at once
      const replacementMap = new Map<string, string>();
      replacements.forEach(({ placeholder, name }) => {
        if (name.toLowerCase() === 'signature') {
          replacementMap.set(placeholder, '');
        } else if (name.startsWith('textarea_')) {
          const fieldName = name.slice('textarea_'.length);
          const placeholderText = camelCaseToPlaceholder(fieldName);
          replacementMap.set(
            placeholder,
            `<textarea name="${fieldName}" placeholder="${placeholderText}" rows="4" class="border border-gray-300 rounded px-2 py-2 text-sm w-full focus:outline-none focus:border-[#01545D] transition-colors" style="display:block; margin-top:4px; box-sizing:border-box; resize:vertical;"></textarea>`,
          );
        } else {
          const placeholderText = camelCaseToPlaceholder(name);
          const inputHTML = `<input type="text" name="${name}" placeholder="${placeholderText}" class="border-0 border-b-2 border-gray-300 bg-transparent px-2 py-2 text-sm focus:outline-none focus:border-[#01545D] transition-colors" style="min-width: min(200px, 100%); max-width: 100%; margin-bottom: 8px; display: block;" />`;
          replacementMap.set(placeholder, inputHTML);
        }
      });

      // Apply all replacements in a single innerHTML update
      let updatedHTML = html;
      replacementMap.forEach((replacement, placeholder) => {
        updatedHTML = updatedHTML.replace(placeholder, replacement);
      });
      container.innerHTML = updatedHTML;
      
      // Mark as processed
      if (!processedPlaceholders.current[formId]) {
        processedPlaceholders.current[formId] = { content: false, signing: false };
      }
      processedPlaceholders.current[formId][containerType] = true;
    },
    [camelCaseToPlaceholder]
  );

  const makeFormElementsInteractive = useCallback(
    (container: HTMLDivElement | null, formId: string) => {
      if (!container) return;

      // Clean up existing event listeners for this form/container combination
      const cleanupKey = `${formId}_${container === contentRefs.current[formId] ? 'content' : 'signing'}`;
      if (eventListenerCleanups.current[cleanupKey]) {
        eventListenerCleanups.current[cleanupKey].forEach(cleanup => cleanup());
        delete eventListenerCleanups.current[cleanupKey];
      }

      const cleanups: Array<() => void> = [];

      // Handle checkboxes
      const checkboxElements = container.querySelectorAll('input[type="checkbox"]');
      checkboxElements.forEach((cb: Element) => {
        const checkbox = cb as HTMLInputElement;
        const identifier = checkbox.getAttribute('name') || checkbox.getAttribute('id');
        if (!identifier) return;

        // Initialize state from HTML if not already set, otherwise use React state
        const currentState = checkboxStates[formId]?.[identifier];
        if (currentState === undefined) {
          // First time - read from HTML
          const isCheckedFromHTML = checkbox.checked;
          setCheckboxStates(prev => ({
            ...prev,
            [formId]: {
              ...(prev[formId] || {}),
              [identifier]: isCheckedFromHTML
            }
          }));
        } else {
          // Use React state to control the checkbox
          checkbox.checked = currentState;
        }

        // Create event handler
        const handleCheckboxChange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          setCheckboxStates(prev => ({
            ...prev,
            [formId]: {
              ...(prev[formId] || {}),
              [identifier]: target.checked
            }
          }));
          // Clear validation errors when user interacts
          setFormValidationErrors(prev => {
            if (prev[formId]?.length) {
              return {
                ...prev,
                [formId]: []
              };
            }
            return prev;
          });
        };

        // Add event listener and store cleanup function
        checkbox.addEventListener('change', handleCheckboxChange);
        cleanups.push(() => checkbox.removeEventListener('change', handleCheckboxChange));
      });

      // Handle radio buttons
      const radioElements = container.querySelectorAll('input[type="radio"]');
      radioElements.forEach((radio: Element) => {
        const radioInput = radio as HTMLInputElement;
        const groupName = radioInput.getAttribute('name');
        if (!groupName) return;

        const value = radioInput.getAttribute('value') || '';
        const currentSelection = radioSelections[formId]?.[groupName];
        
        // Initialize state from HTML if not already set
        if (currentSelection === undefined && radioInput.checked) {
          // First time - read from HTML
          setRadioSelections(prev => ({
            ...prev,
            [formId]: {
              ...(prev[formId] || {}),
              [groupName]: value
            }
          }));
        } else {
          // Use React state to control the radio
          radioInput.checked = currentSelection === value;
        }

        // Create event handler
        const handleRadioChange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          if (target.checked) {
            setRadioSelections(prev => ({
              ...prev,
              [formId]: {
                ...(prev[formId] || {}),
                [groupName]: value
              }
            }));
            // Clear validation errors when user interacts
            setFormValidationErrors(prev => {
              if (prev[formId]?.length) {
                return {
                  ...prev,
                  [formId]: []
                };
              }
              return prev;
            });
          }
        };

        // Add event listener and store cleanup function
        radioInput.addEventListener('change', handleRadioChange);
        cleanups.push(() => radioInput.removeEventListener('change', handleRadioChange));
      });

      // Store cleanup functions for this container
      eventListenerCleanups.current[cleanupKey] = cleanups;
    },
    [checkboxStates, radioSelections]
  );

  // Only process the currently open form to improve performance
  useEffect(() => {
    if (!openForm) return;

    const form = consentForms.find(f => f.id === openForm);
    if (!form) return;

    const contentContainer = contentRefs.current[openForm];
    const signingTextContainer = signingTextRefs.current[openForm];

    // Only replace placeholders once when form is first opened or when signerType changes
    // This prevents losing input field values when checkboxes are clicked
    if (contentContainer) {
      replacePlaceholdersWithInputs(contentContainer, openForm, 'content');
    }
    if (signingTextContainer) {
      replacePlaceholdersWithInputs(signingTextContainer, openForm, 'signing');
    }
    
    // Make checkboxes and radio buttons interactive
    // Use setTimeout to ensure this runs after dangerouslySetInnerHTML updates the DOM
    setTimeout(() => {
      if (contentContainer) {
        makeFormElementsInteractive(contentContainer, openForm);
      }
      if (signingTextContainer) {
        makeFormElementsInteractive(signingTextContainer, openForm);
      }
    }, 0);
  }, [
    openForm,
    consentForms,
    signerType,
    replacePlaceholdersWithInputs,
    makeFormElementsInteractive,
  ]);

  // Reset processed placeholders when form is closed or signerType changes
  useEffect(() => {
    // When signerType changes, reset processed state for all forms so signing text placeholders can be updated
    if (signerType) {
      Object.keys(processedPlaceholders.current).forEach(key => {
        if (key.endsWith('_signing')) {
          delete processedPlaceholders.current[key];
        }
      });
    }
    
    // When a form is closed, reset its processed state so placeholders can be replaced again when reopened
    consentForms.forEach((form) => {
      if (openForm !== form.id) {
        delete processedPlaceholders.current[`${form.id}_content`];
        delete processedPlaceholders.current[`${form.id}_signing`];
      }
    });
  }, [openForm, consentForms, signerType]);

  // Cleanup event listeners when form is closed or component unmounts
  useEffect(() => {
    return () => {
      // Clean up all event listeners on unmount
      Object.values(eventListenerCleanups.current).forEach(cleanups => {
        cleanups.forEach(cleanup => cleanup());
      });
      eventListenerCleanups.current = {};
    };
  }, []);

  // Clean up event listeners when a form is closed
  useEffect(() => {
    consentForms.forEach((form) => {
      if (openForm !== form.id) {
        // Form is closed, clean up its event listeners
        const contentCleanupKey = `${form.id}_content`;
        const signingCleanupKey = `${form.id}_signing`;
        
        [contentCleanupKey, signingCleanupKey].forEach(key => {
          if (eventListenerCleanups.current[key]) {
            eventListenerCleanups.current[key].forEach(cleanup => cleanup());
            delete eventListenerCleanups.current[key];
          }
        });
      }
    });
  }, [openForm, consentForms]);

  const handleSignerChange = (value: 'patient' | 'proxy') => {
    setSignerType(value);
  };

  const scrollToFormError = useCallback((formId: string) => {
    requestAnimationFrame(() => {
      errorAnchorRefs.current[formId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const scrollToPageTop = useCallback(() => {
    requestAnimationFrame(() => {
      pageTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Scroll into view whenever a form gets validation errors (FE or BE).
  useEffect(() => {
    for (const [formId, errs] of Object.entries(formValidationErrors)) {
      if (errs && errs.length > 0) {
        scrollToFormError(formId);
        break;
      }
    }
  }, [formValidationErrors, scrollToFormError]);

  // Scroll to top when a page-level error or workflow error appears.
  useEffect(() => {
    if (error || workflowError) {
      scrollToPageTop();
    }
  }, [error, errorKey, workflowError, scrollToPageTop]);

  const getRadioSelectionsFromDOM = (formId: string): Record<string, string> => {
    const result: Record<string, string> = {};
    const content = contentRefs.current[formId];
    const signing = signingTextRefs.current[formId];
    for (const container of [content, signing]) {
      if (!container) continue;
      container.querySelectorAll('input[type="radio"]').forEach((el) => {
        const input = el as HTMLInputElement;
        if (input.checked) {
          const name = input.getAttribute('name');
          if (name) result[name] = input.value ?? '';
        }
      });
    }
    return result;
  };

  const getCheckboxStatesFromDOM = (formId: string): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    const content = contentRefs.current[formId];
    const signing = signingTextRefs.current[formId];
    for (const container of [content, signing]) {
      if (!container) continue;
      container.querySelectorAll('input[type="checkbox"]').forEach((el) => {
        const input = el as HTMLInputElement;
        const identifier = input.getAttribute('name') || input.getAttribute('id');
        if (identifier) result[identifier] = input.checked;
      });
    }
    return result;
  };

  const validateForm = useCallback((formId: string): ValidationResult => {
    const errors: string[] = [];
    const parsed = parsedElements[formId];
    
    if (!parsed) {
      return { isValid: true, errors: [] }; // No form elements to validate
    }
    
    const checkboxStatesForForm = checkboxStates[formId] || {};
    const domCheckboxStates = getCheckboxStatesFromDOM(formId);
    const effectiveCheckboxStates = { ...checkboxStatesForForm, ...domCheckboxStates };
    const radioSelectionsForForm = radioSelections[formId] || {};
    const domRadioSelections = getRadioSelectionsFromDOM(formId);
    const effectiveRadioSelections = { ...radioSelectionsForForm, ...domRadioSelections };
    
    // Validate mandatory checkboxes (use DOM-backed state so first click works)
    parsed.mandatoryCheckboxes.forEach(identifier => {
      if (!effectiveCheckboxStates[identifier]) {
        const label = parsed.fieldLabels.get(identifier) ?? identifier;
        errors.push(`Required checkbox "${label}" must be checked`);
      }
    });

    // Validate mandatory radio groups (use DOM-backed selections so first click works)
    parsed.mandatoryRadioGroups.forEach(groupName => {
      const selectedValue = effectiveRadioSelections[groupName];
      if (!selectedValue || selectedValue.trim() === '') {
        const label = parsed.groupLabels.get(groupName) ?? groupName;
        errors.push(`Required field "${label}" must have a selection`);
      }
    });

    // Validate required checkbox groups (at least one must be checked)
    parsed.mandatoryCheckboxGroups.forEach((ids, groupName) => {
      const anyChecked = ids.some((id) => effectiveCheckboxStates[id] === true);
      if (!anyChecked) {
        const label = parsed.groupLabels.get(groupName) ?? groupName;
        errors.push(`At least one option must be selected for "${label}"`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [parsedElements, checkboxStates, radioSelections]);

  const collectPlaceholderData = (formId: string): Record<string, string | boolean> => {
    const placeholderData: Record<string, string | boolean> = {};
    
    // Collect text inputs (existing functionality)
    const contentContainer = contentRefs.current[formId];
    if (contentContainer) {
      const inputs = contentContainer.querySelectorAll('input[type="text"]');
      inputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        const name = inputElement.name;
        const value = inputElement.value.trim();
        if (name && value) {
          placeholderData[name] = value;
        }
      });
      // Collect textarea values with "textarea_" prefix
      contentContainer.querySelectorAll('textarea').forEach((el) => {
        const ta = el as HTMLTextAreaElement;
        const name = ta.getAttribute('name') || ta.getAttribute('id');
        if (name) {
          placeholderData[`textarea_${name}`] = ta.value;
        }
      });
    }
    
    const signingTextContainer = signingTextRefs.current[formId];
    if (signingTextContainer) {
      const inputs = signingTextContainer.querySelectorAll('input[type="text"]');
      inputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        const name = inputElement.name;
        const value = inputElement.value.trim();
        if (name && value) {
          placeholderData[name] = value;
        }
      });
      // Collect textarea values with "textarea_" prefix
      signingTextContainer.querySelectorAll('textarea').forEach((el) => {
        const ta = el as HTMLTextAreaElement;
        const name = ta.getAttribute('name') || ta.getAttribute('id');
        if (name) {
          placeholderData[`textarea_${name}`] = ta.value;
        }
      });
    }
    
    // Collect checkbox states with "checkbox_" prefix (merge state with DOM so payload is current on first submit)
    const checkboxStatesForForm = checkboxStates[formId] || {};
    const domCheckboxStates = getCheckboxStatesFromDOM(formId);
    const effectiveCheckboxStates = { ...checkboxStatesForForm, ...domCheckboxStates };
    Object.entries(effectiveCheckboxStates).forEach(([identifier, checked]) => {
      placeholderData[`checkbox_${identifier}`] = checked;
    });
    
    // Collect radio selections with "radio_" prefix (merge state with DOM so payload is current on first submit)
    const radioSelectionsForForm = radioSelections[formId] || {};
    const domRadioSelections = getRadioSelectionsFromDOM(formId);
    const effectiveRadioSelections = { ...radioSelectionsForForm, ...domRadioSelections };
    Object.entries(effectiveRadioSelections).forEach(([groupName, value]) => {
      if (value && value.trim() !== '') {
        placeholderData[`radio_${groupName}`] = value;
      }
    });
    
    return placeholderData;
  };

  const handleSignForm = async (formId: string) => {
    if (!appointmentId) {
      setError('Missing appointment id in URL.');
      return;
    }

    const signatureHandle = signatureRefs.current[formId];
    if (!signatureHandle) {
      setError('Signature canvas not found. Please try again.');
      return;
    }
    
    const signatureDataUrl = signatureHandle.getSignature();
    if (!signatureDataUrl) {
      setError('Please provide a signature before signing the form.');
      return;
    }

    // Validate form before submission
    const validation = validateForm(formId);
    if (!validation.isValid) {
      setFormValidationErrors(prev => ({
        ...prev,
        [formId]: validation.errors
      }));
      return;
    }

    // Clear validation errors
    setFormValidationErrors(prev => ({
      ...prev,
      [formId]: []
    }));

    // Extract base64 data from data URL (remove "data:image/png;base64," prefix)
    const base64Signature = signatureDataUrl.replace(/^data:image\/png;base64,/, '');

    // Collect placeholder data from form inputs
    const placeholderData = collectPlaceholderData(formId);

    // Find the form to get its ID
    const form = consentForms.find(f => f.id === formId);
    if (!form) {
      setError('Form not found. Please try again.');
      return;
    }

    if (inFlightSignFormsRef.current.has(formId)) return;
    inFlightSignFormsRef.current.add(formId);

    setSigningForms((prev) => {
      const newSet = new Set(prev);
      newSet.add(formId);
      return newSet;
    });
    setError(null);

    try {
      await withLoader('Signing consent form...', async () => {
        const payload = {
          consentFormId: formId,
          signedByPatient: signerType === 'patient',
          signatureImage: base64Signature,
          placeholderData: placeholderData,
        };

        await axiosInstance.post(
          `/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/consent-forms/sign`,
          payload,
          { headers: { client: 'axis', domain: 'axis' } }
        );

        // Optimistically mark the matching CONSENT_FORM step row completed
        // so the bar reacts instantly, then refetch the workflow for the
        // authoritative state. We stay on this page after signing a single
        // consent form, so refreshWorkflow is the only thing that will
        // update the progress bar.
        markStepCompleted(
          (s) =>
            s.workflowType === WorkflowType.CONSENT_FORM &&
            String(s.entityId) === String(formId),
        );
        await refreshWorkflow();

        // Mark form as signed and submitted
        setSignedForms((prev) => {
          const newSet = new Set(prev);
          newSet.add(formId);
          return newSet;
        });
        setSubmittedForms((prev) => {
          const newSet = new Set(prev);
          newSet.add(formId);
          return newSet;
        });

        // Auto-collapse the form after successful signing
        if (openForm === formId) {
          setOpenForm(null);
        }
      });
    } catch (err: any) {
      const errorCode = getApiErrorCode(err);
      const errorMessage = getApiErrorMessage(err, '');
      const hasMissingPlaceholder =
        errorCode === 'MISSING_PLACEHOLDER_DATA' ||
        errorMessage.toLowerCase().includes('missing required placeholder');

      if (hasMissingPlaceholder) {
        setFormValidationErrors(prev => ({
          ...prev,
          [formId]: ['Please fill all the fields in the form.'],
        }));
      } else if (err.response?.status === 400) {
        setFormValidationErrors(prev => ({
          ...prev,
          [formId]: [errorMessage || 'Validation failed'],
        }));
      } else if (err.response?.status === 409) {
        setError('Workflow state error. Please refresh and try again.');
      } else {
        setError(getApiErrorMessage(err, 'Unable to sign the consent form. Please try again.'));
      }
    } finally {
      setSigningForms((prev) => {
        const newSet = new Set(prev);
        newSet.delete(formId);
        return newSet;
      });
      inFlightSignFormsRef.current.delete(formId);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!appointmentId) {
      setError('Missing appointment id in URL.');
      return;
    }

    // Check if all required forms are signed and submitted
    const requiredForms = consentForms.filter(form => form.required !== false);
    const allRequiredSigned = requiredForms.every(form => submittedForms.has(form.id));

    if (!allRequiredSigned) {
      const unsignedForms = requiredForms.filter(form => !submittedForms.has(form.id));
      setError(`Please sign all required consent forms before submitting. Missing: ${unsignedForms.map(f => f.name).join(', ')}`);
      return;
    }

    // All forms are already submitted via individual sign buttons
    // Navigate to completion page
    setSubmitting(true);
    setError(null);
    try {
      await withLoader('Submitting...', async () => {
        navigate(`/appointments/${appointmentId}/complete`);
      });
    } catch (err) {
      setError('Unable to complete your check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveAndExit = async () => {
    if (!appointmentId) {
      setError('Missing appointment id in URL.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      // Save current progress (forms are already saved via individual sign buttons)
      // Navigate to completion page
      navigate(`/appointments/${appointmentId}/complete`);
    } catch (err) {
      setError('Unable to save your information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <CheckinLayout
      title="Review & Sign"
      subtitle="Please review and sign all required consent forms"
    >
        <div ref={pageTopRef} />
        {(error || workflowError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert
              type="error"
              onClose={() => {
                setError(null);
              }}
            >
              {workflowError || error}
            </Alert>
          </div>
        )}
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-4 sm:p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          <section className="flex flex-col gap-3">
            <div className="text-base font-semibold text-[#1C1C1C]">
              Who is signing? <span className="text-red-500">*</span>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <button
                type="button"
                onClick={() => handleSignerChange('patient')}
                className={
                  'flex-1 py-3 px-5 rounded-md border text-base font-medium ' +
                  (signerType === 'patient'
                    ? 'border-[#01545D] bg-[#01545D] text-white'
                    : 'border-[#DCE1E8] bg-white text-[#1C1C1C]')
                }
              >
                I am patient
              </button>
              <button
                type="button"
                onClick={() => handleSignerChange('proxy')}
                className={
                  'flex-1 py-3 px-5 rounded-md border text-base font-medium ' +
                  (signerType === 'proxy'
                    ? 'border-[#01545D] bg-[#01545D] text-white'
                    : 'border-[#DCE1E8] bg-white text-[#1C1C1C]')
                }
              >
                I am signing on behalf of patient
              </button>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <div className="text-lg font-semibold text-[#01545D]">
              Consent Forms
            </div>

            {allForms.length === 0 && !loading && (
              <div className="bg-[#E0F2F3] border border-[#01545D] rounded-lg px-6 py-5 flex items-center gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#01545D] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-lg font-semibold text-[#01545D] mb-1">
                    You're All Set!
                  </div>
                  <div className="text-sm text-[#1C1C1C]">
                    There are no consent forms to review at this time. You're all set to move ahead.
                  </div>
                </div>
              </div>
            )}

            {allForms.map((form) => (
              <ConsentFormItem
                key={form.id}
                form={form}
                isOpen={openForm === form.id}
                isSigned={signedForms.has(form.id)}
                signerType={signerType}
                formValidationErrors={formValidationErrors[form.id]}
                signingForms={signingForms}
                onToggleOpen={() => setOpenForm(openForm === form.id ? null : form.id)}
                onSign={handleSignForm}
                contentRef={(el) => {
                  if (el) contentRefs.current[form.id] = el;
                }}
                signingTextRef={(el) => {
                  if (el) signingTextRefs.current[form.id] = el;
                }}
                signatureRef={(el) => {
                  if (el) signatureRefs.current[form.id] = el;
                }}
                errorAnchorRef={(el) => {
                  if (el) {
                    errorAnchorRefs.current[form.id] = el;
                  } else {
                    delete errorAnchorRefs.current[form.id];
                  }
                }}
              />
            ))}
          </section>

          <div className="flex items-start gap-3 bg-[#FFF3CD] border border-[#FFC107] rounded-lg px-4 py-3 mt-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FFC107] text-white text-xs font-bold">
              !
            </div>
            <div className="text-sm text-[#856404]">
              Please sign all required consent forms before submitting
            </div>
          </div>

          <div className="flex gap-5 w-full mt-8">
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => {
                const previousStep = getPreviousStep(location.pathname);
                if (previousStep) {
                  navigate(previousStep.stepUrl);
                } else if (appointmentId) {
                  // Fallback to wellness-check if no previous step found
                  navigate(`/appointments/${appointmentId}/wellness-check`);
                }
              }}
              disabled={!!workflowError || submitting || loading}
              className="flex-1"
            >
              Back
            </AppButton>
            {/* <AppButton
              variant="secondary"
              type="button"
              onClick={handleSaveAndExit}
              disabled={submitting || loading}
              className="flex-1"
            >
              {submitting ? 'Saving...' : 'Save & Exit'}
            </AppButton> */}
            <AppButton type="submit" disabled={!!workflowError || submitting || loading} className="flex-1">
              {submitting ? 'Submitting...' : 'Submit'}
            </AppButton>
          </div>
        </form>
    </CheckinLayout>
  );
};

export default ReviewAndSign;
